/**
 * Pashion: cart upsell fallback via Shopify product recommendations.
 *
 * Broadcast's Upsell block (`cart-block-upsell.liquid`) only shows products
 * referenced by the `theme.upsell` / `theme.upsell_list` metafields of the
 * items in the cart. When those are empty, this fills the same block with
 * Shopify's recommendations for the most recently added item, rendered by
 * `sections/api-cart-upsell.liquid` so the cards are identical to metafield
 * upsells (quick-add, image, price).
 *
 * Runs only when the cart drawer or cart page is rendered; one request to
 * /recommendations/products per cart state. Skipped products are remembered
 * in sessionStorage under Broadcast's own `skip_upsell_products` key.
 */
(function () {
  'use strict';

  var HOLDER = '[data-upsell-products][data-upsell-recommend="true"]';
  var CARD = '[data-quick-add-holder]';
  var WIDGET = '[data-upsell-widget]';
  var SKIP_KEY = 'skip_upsell_products';
  var route = (window.theme && window.theme.routes && window.theme.routes.product_recommendations_url) || '/recommendations/products';

  function skipped() {
    try {
      var raw = window.sessionStorage.getItem(SKIP_KEY) || '';
      return raw.split(',').filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function remember(id) {
    try {
      var list = skipped();
      if (list.indexOf(String(id)) === -1) list.push(String(id));
      window.sessionStorage.setItem(SKIP_KEY, list.join(','));
    } catch (e) {}
  }

  function openWidget(holder) {
    var widget = holder.closest(WIDGET);
    if (!widget) return;
    widget.classList.remove('is-hidden', 'hidden'); // Broadcast toggles `is-hidden`
    if (widget.hasAttribute('data-upsell-auto-open') && !widget.hasAttribute('open')) {
      widget.setAttribute('open', 'true');
      var body = widget.querySelector('[data-collapsible-body]');
      if (body) body.style.height = 'auto';
    }
  }

  function hideWidgetIfEmpty(holder) {
    if (holder.querySelector(CARD)) return;
    var widget = holder.closest(WIDGET);
    if (widget) widget.classList.add('is-hidden');
  }

  function bindSkip(holder) {
    holder.querySelectorAll('[data-skip-upsell-product]').forEach(function (button) {
      if (button.__pashionSkip) return;
      button.__pashionSkip = true;
      button.addEventListener('click', function (event) {
        event.preventDefault();
        var card = button.closest(CARD);
        if (!card) return;
        remember(card.getAttribute('data-quick-add-holder'));
        card.remove();
        hideWidgetIfEmpty(holder);
      });
    });
  }

  function fill(holder) {
    if (holder.__pashionFilling) return;
    if (holder.querySelector(CARD)) return; // metafield upsells (or our own cards) already present

    holder.__pashionFilling = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          holder.__pashionFilling = false;
          return;
        }
        var key = cart.items.map(function (i) { return i.key; }).join('|');
        if (holder.getAttribute('data-upsell-recommend-key') === key) {
          holder.__pashionFilling = false;
          return; // already tried for this exact cart; nothing to add
        }
        holder.setAttribute('data-upsell-recommend-key', key);

        var limit = parseInt(holder.getAttribute('data-upsell-limit'), 10) || 3;
        var preferred = holder.getAttribute('data-upsell-intent') || 'complementary';
        var other = preferred === 'complementary' ? 'related' : 'complementary';

        // Try the newest item first with the preferred intent, then the other
        // intent, then older items. Heel kits have no "complementary" products in
        // Search & Discovery, so a cart whose newest line is a kit would otherwise
        // come up empty. At most 4 requests, and only one cart state at a time.
        var attempts = [];
        cart.items.slice(0, 2).forEach(function (item) {
          attempts.push({ id: item.product_id, intent: preferred });
          attempts.push({ id: item.product_id, intent: other });
        });

        var skip = skipped();

        function tryNext(index) {
          if (index >= attempts.length) return;
          var a = attempts[index];
          var url = route + '?section_id=api-cart-upsell&product_id=' + a.id + '&limit=' + limit + '&intent=' + a.intent;
          return fetch(url)
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var items = doc.querySelector('[data-api-upsell-items]');
              if (items) {
                items.querySelectorAll(CARD).forEach(function (card) {
                  if (skip.indexOf(card.getAttribute('data-quick-add-holder')) !== -1) card.remove();
                });
              }
              if (!items || !items.querySelector(CARD)) return tryNext(index + 1);
              if (holder.querySelector(CARD)) return; // cart changed meanwhile

              holder.innerHTML = items.innerHTML;
              bindSkip(holder);
              openWidget(holder);
            });
        }

        return tryNext(0);
      })
      .catch(function () {})
      .then(function () {
        holder.__pashionFilling = false;
      });
  }

  function watch(holder) {
    if (holder.__pashionWatched) return;
    holder.__pashionWatched = true;
    // Broadcast replaces the holder's contents after every cart change
    var observer = new MutationObserver(function () {
      window.requestAnimationFrame(function () { fill(holder); });
    });
    observer.observe(holder, { childList: true });
    fill(holder);
  }

  function init() {
    document.querySelectorAll(HOLDER).forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('theme:cart-drawer:open', init);
  document.addEventListener('theme:cart:change', init);
})();
